import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import ReplayIcon from '@mui/icons-material/Replay';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import UndoOutlinedIcon from '@mui/icons-material/UndoOutlined';
import { Button, Tooltip } from '@mui/material';
import { SaveButton, Toolbar, useRecordContext } from 'react-admin';
import { AppArt, AppArtBucket, AppArtStatus } from '../types';
import { toBriefingPayload } from '../lib/briefing';
import { useArtFiles } from './ArtFilesContext';

/**
 * `alwaysEnable` nos botões: por padrão o `<SaveButton>` se desabilita quando o
 * form está pristine (`!isDirty`), e aqui o trabalho principal — subir logo e
 * foto — acontece FORA do form, sem sujá-lo. Sem isso, quem só anexa arquivos
 * fica sem conseguir salvar.
 *
 * Os avisos de qualidade dos arquivos (logo pequena, sem transparência, HEIC)
 * NÃO entram em nenhuma condição daqui: são sinal para quem está atendendo,
 * não trava. A única regra que segura o envio é não haver logo nenhuma.
 *
 * Os botões que mudam o status usam `transform` para gravar o briefing e mover
 * o status na MESMA request. `type="button"` é obrigatório junto com
 * `transform`: no `<SaveButton>` o transform só é aplicado dentro do
 * `handleClick`, e esse ramo só roda quando o type é "button". Com o "submit"
 * padrão o clique cai no submit nativo do form, o transform é ignorado e o
 * status nunca sai do lugar — o próprio react-admin emite um warning sobre isso.
 */
const ArtToolbar = () => {
  const record = useRecordContext<AppArt>();
  const { buckets } = useArtFiles();
  if (!record) { return null; }

  const fileList = record.files || [];
  const liveCount = (bucket: AppArtBucket) =>
    buckets[bucket]?.count ?? fileList.filter(file => file.bucket === bucket).length;

  const logoCount = liveCount(AppArtBucket.logo);

  if (record.status === AppArtStatus.draft) {
    return (
      <Toolbar sx={{ display: 'flex', gap: 2 }}>
        <SaveButton label="Salvar rascunho" variant="outlined" icon={<></>} alwaysEnable />

        {/* Toda rede sem registro nasce em rascunho. Para as que já têm app no
            ar, feito antes disto aqui existir, isso é ruído: marcar como fora
            do fluxo tira da fila sem inventar um material que ninguém coletou. */}
        <Tooltip title="Para redes que já têm a arte pronta, feita antes ou fora deste fluxo. Sai da lista de pendências.">
          <span>
            <SaveButton
              label="Arte já feita fora daqui"
              variant="outlined"
              color="inherit"
              icon={<ArchiveOutlinedIcon />}
              type="button"
              alwaysEnable
              transform={() => ({ status: AppArtStatus.legacy })}
            />
          </span>
        </Tooltip>

        {logoCount > 0 ? (
          <SaveButton
            label="Enviar para geração"
            icon={<SendOutlinedIcon />}
            type="button"
            alwaysEnable
            transform={data => ({ ...toBriefingPayload(data), status: AppArtStatus.submitted })}
          />
        ) : (
          <Tooltip title="Envie ao menos uma logo da rede para mandar para a geração.">
            <span>
              <Button variant="contained" startIcon={<SendOutlinedIcon />} disabled>
                Enviar para geração
              </Button>
            </span>
          </Tooltip>
        )}
      </Toolbar>
    );
  }

  const modelCount = liveCount(AppArtBucket.model);
  const hasChosenModel = buckets[AppArtBucket.model]?.hasPrimary
    ?? fileList.some(file => file.bucket === AppArtBucket.model && file.isPrimary);

  // Enviado: destravar para editar de novo, ou anunciar que os modelos saíram.
  if (record.status === AppArtStatus.submitted) {
    return (
      <Toolbar sx={{ display: 'flex', gap: 2 }}>
        <SaveButton
          label="Voltar para rascunho"
          variant="outlined"
          color="warning"
          icon={<UndoOutlinedIcon />}
          type="button"
          alwaysEnable
          transform={data => ({ ...toBriefingPayload(data), status: AppArtStatus.draft })}
        />

        {/* O caminho normal é o gerador mover o status sozinho depois de subir
            os modelos. Este botão é a saída manual — modelo subido na mão pela
            tela, ou gerador que subiu o arquivo e morreu antes do PATCH. */}
        {modelCount > 0 ? (
          <SaveButton
            label="Marcar modelos como prontos"
            icon={<CheckCircleOutlineIcon />}
            type="button"
            alwaysEnable
            transform={() => ({ status: AppArtStatus.modelsReady })}
          />
        ) : (
          <Tooltip title="O gerador faz isso sozinho ao subir os modelos. Para fazer na mão, suba ao menos um modelo no card “Modelos gerados”.">
            <span>
              <Button variant="contained" startIcon={<CheckCircleOutlineIcon />} disabled>
                Marcar modelos como prontos
              </Button>
            </span>
          </Tooltip>
        )}
      </Toolbar>
    );
  }

  // Modelos na mesa: confirmar qual o cliente escolheu, ou pedir outra rodada.
  if ([AppArtStatus.modelsReady, AppArtStatus.changesRequested].includes(record.status)) {
    const pending = record.status === AppArtStatus.changesRequested;
    return (
      <Toolbar sx={{ display: 'flex', gap: 2 }}>
        {/* `changes_requested` existia no fluxo e no banco mas nenhum botão
            chegava nele — a rede só voltava para "enviado", que não conta a
            ninguém POR QUE ela voltou. Aqui o pedido do cliente fica dito. */}
        {pending ? (
          <SaveButton
            label="Cancelar pedido de nova rodada"
            variant="outlined"
            icon={<UndoOutlinedIcon />}
            type="button"
            alwaysEnable
            transform={() => ({ status: AppArtStatus.modelsReady })}
          />
        ) : (
          <SaveButton
            label="Pedir nova rodada"
            variant="outlined"
            color="warning"
            icon={<ReplayIcon />}
            type="button"
            alwaysEnable
            transform={() => ({ status: AppArtStatus.changesRequested })}
          />
        )}

        {hasChosenModel ? (
          <SaveButton
            label="Confirmar modelo escolhido"
            icon={<DoneAllIcon />}
            type="button"
            alwaysEnable
            transform={() => ({ status: AppArtStatus.modelChosen })}
          />
        ) : (
          <Tooltip title="Marque com a ★ qual modelo o cliente escolheu.">
            <span>
              <Button variant="contained" startIcon={<DoneAllIcon />} disabled>
                Confirmar modelo escolhido
              </Button>
            </span>
          </Tooltip>
        )}
      </Toolbar>
    );
  }

  // Modelo escolhido: só falta fechar a arte.
  if (record.status === AppArtStatus.modelChosen) {
    return (
      <Toolbar sx={{ display: 'flex', gap: 2 }}>
        <SaveButton
          label="Voltar para modelos"
          variant="outlined"
          color="warning"
          icon={<UndoOutlinedIcon />}
          type="button"
          alwaysEnable
          transform={() => ({ status: AppArtStatus.modelsReady })}
        />

        {/* "Publicado" seria alegação sobre a loja, que não passa por esta
            tela. O que esta tela sabe dizer é que a arte está fechada. */}
        <SaveButton
          label="Marcar arte como definida"
          icon={<TaskAltIcon />}
          color="success"
          type="button"
          alwaysEnable
          transform={() => ({ status: AppArtStatus.published })}
        />
      </Toolbar>
    );
  }

  // Arte definida é o fim da linha; reabrir é caso raro e deliberado.
  if (record.status === AppArtStatus.published) {
    return (
      <Toolbar>
        <SaveButton
          label="Reabrir (voltar para modelo escolhido)"
          variant="outlined"
          color="warning"
          icon={<UndoOutlinedIcon />}
          type="button"
          alwaysEnable
          transform={() => ({ status: AppArtStatus.modelChosen })}
        />
      </Toolbar>
    );
  }

  // Fora do fluxo não tem passo seguinte — só a volta, se a rede pedir arte nova.
  if (record.status === AppArtStatus.legacy) {
    return (
      <Toolbar>
        <SaveButton
          label="Começar arte nova"
          variant="outlined"
          icon={<ReplayIcon />}
          type="button"
          alwaysEnable
          transform={() => ({ status: AppArtStatus.draft })}
        />
      </Toolbar>
    );
  }

  return null;
};

export default ArtToolbar;
